import React from 'react'
import get from 'lodash/get'
import className from 'classnames'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import * as __axios from 'axios'
import moment from 'moment'

import {useAsync} from '@lib/hooks'
import {sendEmail} from '@lib/services'
import {axios, t} from '@lib/helpers'
import {useUTMContext} from '@lib/gtm'

const Hamburger = dynamic(() => import('@components/Hamburger'))
const ImageLoader = dynamic(() => import('@components/ImageLoader'))
const Meta = dynamic(() => import('@components/Meta'))

const delay = (ms) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      resolve()
    }, ms)
  })
}

const FaceBlur = ({isRecaptchaValidated}) => {
  const $img = React.useRef()
  const __imgWidth = React.useRef()
  const $imgCover = React.useRef()
  const $knob = React.useRef()
  const $blank = React.useRef()

  const __clientX = React.useRef(-1)
  const __knobLeft = React.useRef(-1)
  const __windowWidth = React.useRef(-1)

  const [isActiveNav, isActiveNavSet] = React.useState(false)

  const [isAuto, isAutoSet] = React.useState(false)
  const [knobLeft, knobLeftSet] = React.useState(-1)

  const [isHovering, isHoveringSet] = React.useState(false)

  const [isUploading, isUploadingSet] = React.useState(false)
  const [isUploadedFirstTime, isUploadedFirstTimeSet] = React.useState(false)
  const [isError, isErrorSet] = React.useState(false)
  const [imgSrc, imgSrcSet] = React.useState(
    '/images/FaceBlur__upload__right__img.jpg'
  )
  const [imgBlurSrc, imgBlurSrcSet] = React.useState()
  const [imgDisplayMode, imgDisplayModeSet] = React.useState(1)

  const {run: runSendEmail, isLoading, isSuccess} = useAsync(sendEmail)
  const ref = React.useRef()

  const {data: utm} = useUTMContext()

  const widthSet = () => {
    __windowWidth.current =
      typeof window != 'undefined' || typeof document != 'undefined'
        ? window.innerWidth ||
          document.documentElement.clientWidth ||
          document.body.clientWidth
        : -1
    __imgWidth.current = $img.current.getBoundingClientRect().width
  }

  const __windowTO = React.useRef()
  const __windowInnerWidthPrev = React.useRef(
    typeof window != 'undefined' ? window.innerWidth : -1
  )

  const onResize = (e) => {
    window.clearTimeout(__windowTO.current)
    __windowTO.current = window.setTimeout(() => {
      if (__windowInnerWidthPrev.current != window.innerWidth) {
        __windowInnerWidthPrev.current = window.innerWidth
        widthSet()
        knobLeftSet(__imgWidth.current * 0.4)
      }
    }, 300)
  }

  React.useEffect(() => {
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [])

  React.useEffect(() => {
    ;(async () => {
      await delay(1000)

      widthSet()
      knobLeftSet(__imgWidth.current * 0.6)

      await delay(1000)

      isAutoSet(true)
      knobLeftSet(__imgWidth.current * 0.25)

      await delay(1000)

      knobLeftSet(__imgWidth.current * 0.4)

      await delay(1000)

      isAutoSet(false)
    })()
  }, [])

  React.useEffect(() => {
    if (isSuccess && ref.current) {
      ref.current.reset()
    }
  }, [isSuccess])

  const handleSubmit = async (e) => {
    e.preventDefault()

    const {name, email, tel} = e.target.elements

    const emailData = {
      to: process.env.SALES_EMAIL,
      subject: `[${window.location.host}] ${email.value} wants to contact with you`,
      message: `
        Name: ${name.value}
        Email: ${email.value}
        Tel: ${tel.value}
      )}`,
    }

    const reqBody = {
      email: email.value,
      name: name.value,
      phone: tel.value,
      url: window.location.href,
    }

    runSendEmail({
      emailData: {...emailData, path: process.env.EMAIL_API_URL},
      reqBody: reqBody,
      utm,
    })
  }

  return (
    <React.Fragment>
      <Meta
        title={`Protect your users privacy easily – ${t('layout.title')}`}
        description='Pixelate faces using AI tool with built-in intelligent face detector. Data Wow Face Blur is a privacy service that will make sure you comply with privacy laws and protect your users.'
        image='banner--faceblur.png'
      />

      <div className='FaceBlur'>
        <div className='FaceBlur__nav'>
          <div className='container'>
            <h1 className='FaceBlur__nav__h1'>
              <a
                className='FaceBlur__nav__h1__a'
                onClick={() => {
                  window.history.pushState(null, null, '/demo/faceblur')
                  window.scroll({top: 0, behavior: 'smooth'})
                }}
              >
                FaceBlur
              </a>
            </h1>

            <div
              className={className('FaceBlur__nav__links', {
                'FaceBlur__nav__links--active': isActiveNav,
              })}
            >
              {[
                {
                  label: `${t('pages.demo.shared.nav.why')} Faceblur`,
                  anchor: '#why',
                },
                {
                  label: t('pages.demo.shared.nav.contact'),
                  anchor: '#contact',
                },
                {
                  label: t('pages.demo.shared.nav.upload_image'),
                  klass: 'button',
                  anchor: '#upload',
                },
              ].map((x, i) => (
                <a
                  key={i}
                  className={className('FaceBlur__nav__links__a', {
                    [`FaceBlur__nav__links__a--${get(x, 'klass')}`]: get(
                      x,
                      'klass'
                    ),
                  })}
                  onClick={(e) => {
                    if (x.anchor) {
                      e.preventDefault()

                      isActiveNavSet(false)

                      window.history.pushState(null, null, get(x, 'anchor'))
                      document
                        .querySelector(get(x, 'anchor'))
                        .scrollIntoView({behavior: 'smooth'})
                    }
                  }}
                >
                  <div className='FaceBlur__nav__links__a__wrapper'>
                    <span className='FaceBlur__nav__links__a__span'>
                      {get(x, 'label')}
                    </span>
                  </div>
                </a>
              ))}
            </div>

            <Hamburger
              klass='FaceBlur__nav__handle'
              isActive={isActiveNav}
              isActiveSet={isActiveNavSet}
            />
          </div>
        </div>

        <div className='FaceBlur__slider'>
          <img
            className='FaceBlur__slider__blank'
            alt='FaceBlur__slider__blank'
            src='/images/blank.png'
            ref={$blank}
          />

          <div className='FaceBlur__slider__wrapper'>
            <img
              ref={$img}
              className='FaceBlur__slider__img'
              alt='FaceBlur__slider__img'
              src='/images/FaceBlur__slider__img--blurred.jpg'
            />

            <div className='FaceBlur__slider__cover'>
              <div
                className={className('FaceBlur__slider__cover__img', {
                  'FaceBlur__slider__cover__img--auto': isAuto,
                })}
                ref={$imgCover}
                style={{
                  backgroundImage: 'url(/images/FaceBlur__slider__img.jpg)',
                  width: `${knobLeft}px`,
                }}
              ></div>
            </div>

            {(() => {
              const getDestinationX = (destinationX) => {
                if (destinationX < 0) {
                  return 0
                }

                if (destinationX > __imgWidth.current) {
                  return __imgWidth.current
                }

                return destinationX
              }

              const onDragStart = ({_clientX}) => {
                if (_clientX) {
                  __clientX.current = _clientX
                  __knobLeft.current = knobLeft
                }
              }

              const onDrag = ({_clientX}) => {
                if (_clientX > 0) {
                  // `_clientX` will be 0 once mouse up.
                  const destinationX = getDestinationX(
                    __knobLeft.current + _clientX - __clientX.current
                  )

                  // Directly set the styles because using a state here
                  // will cause React to re-render too many times.
                  $imgCover.current.style.width = `${destinationX}px`
                  $knob.current.style.left = `${destinationX}px`
                }
              }

              const onDragEnd = ({_clientX}) => {
                knobLeftSet(
                  getDestinationX(
                    __knobLeft.current + _clientX - __clientX.current
                  )
                )
              }

              return (
                <a
                  className={className('FaceBlur__slider__knob', {
                    'FaceBlur__slider__knob--hidden': knobLeft < 0,
                    'FaceBlur__slider__knob--auto': isAuto,
                  })}
                  style={{
                    left: `${knobLeft}px`,
                  }}
                  ref={$knob}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setDragImage($blank.current, 0, 0)

                    onDragStart({_clientX: e.clientX})
                  }}
                  onDrag={(e) => {
                    onDrag({
                      _clientX: e.clientX,
                    })
                  }}
                  onDragEnd={(e) => {
                    onDragEnd({
                      _clientX: e.clientX,
                    })
                  }}
                  onTouchStart={(e) => {
                    onDragStart({_clientX: get(e, 'touches[0].clientX')})
                  }}
                  onTouchMove={(e) => {
                    onDrag({
                      _clientX: get(e, 'touches[0].clientX'),
                    })
                  }}
                  onTouchEnd={(e) => {
                    onDragEnd({
                      _clientX: get(e, 'changedTouches[0].clientX'),
                    })
                  }}
                >
                  <span className='FaceBlur__slider__knob__handle' />
                </a>
              )
            })()}
          </div>

          <div className='FaceBlur__slider__content'>
            <p className='FaceBlur__slider__content__p'>
              {t('pages.demo.faceblur.hero.title')}
            </p>

            <h2
              className='FaceBlur__slider__content__h2'
              dangerouslySetInnerHTML={{
                __html: t('pages.demo.faceblur.hero.description'),
              }}
            />

            <div className='Buttons'>
              <a
                className='FaceBlur__button'
                onClick={(e) => {
                  e.preventDefault()

                  window.history.pushState(null, null, '#upload')
                  document
                    .querySelector('#upload')
                    .scrollIntoView({behavior: 'smooth'})
                }}
              >
                {t('pages.demo.shared.nav.upload_image')}
              </a>
            </div>

            <small
              className='FaceBlur__slider__content__small'
              dangerouslySetInnerHTML={{
                __html: t('pages.demo.faceblur.hero.content'),
              }}
            />
          </div>
        </div>

        <ImageLoader
          klass='FaceBlur__sub-bg'
          src='/images/FaceBlur__sub-bg.png'
          forceActive
        />

        <div className='Anchor' id='why'></div>
        <div className='FaceBlur__why'>
          <div className='container'>
            <ImageLoader
              klass='FaceBlur__why__img'
              src='/images/FaceBlur__why__img.png'
            />

            <div className='FaceBlur__why__right'>
              <h2 className='FaceBlur__why__right__h2'>Why we use faceblur</h2>

              <p className='FaceBlur__why__right__p'>
                {t('pages.demo.faceblur.why.description.0')}
              </p>

              <p className='FaceBlur__why__right__p'>
                {t('pages.demo.faceblur.why.description.1')}
              </p>
            </div>
          </div>
        </div>

        <div className='Anchor' id='upload'></div>
        <div className='FaceBlur__upload'>
          <div className='container'>
            <div className='FaceBlur__upload__wrapper'>
              <div className='FaceBlur__upload__wrapper-inner'>
                <h2
                  className='FaceBlur__upload__h2'
                  dangerouslySetInnerHTML={{
                    __html: t('pages.demo.faceblur.upload.title'),
                  }}
                />

                <p className='FaceBlur__upload__p'>
                  {t('pages.demo.faceblur.upload.description')}
                </p>

                {(() => {
                  const onError = () => {
                    isErrorSet(true)
                    imgDisplayModeSet(0)
                  }

                  const upload = ({file}) => {
                    if (
                      file &&
                      isRecaptchaValidated &&
                      get(file, 'type').match(/jpg|jpeg|png/g) &&
                      !isUploading
                    ) {
                      const formData = new FormData()
                      formData.append('image', file)

                      isErrorSet(false)
                      isUploadingSet(true)
                      imgSrcSet(window.URL.createObjectURL(file))

                      if (__windowWidth.current <= 1320) {
                        document
                          .querySelector('#result')
                          .scrollIntoView({behavior: 'smooth'})
                      }

                      axios
                        .post('/face_blurs', formData)
                        .then((res) => {
                          console.log('res', res)
                          if (get(res, 'data.data.status') == 'error') {
                            onError()
                            return
                          }

                          imgBlurSrcSet(get(res, 'data.data.presigned_url'))
                          imgDisplayModeSet(1)
                        })
                        .catch((err) => {
                          console.log(err)
                          onError()
                        })
                        .finally(() => {
                          isUploadedFirstTimeSet(true)
                          isUploadingSet(false)
                        })
                    }
                  }

                  return (
                    <label
                      disabled={!isRecaptchaValidated || isUploading}
                      className={className('FaceBlur__upload__box', {
                        'FaceBlur__upload__box--active': isHovering,
                      })}
                    >
                      <input
                        type='file'
                        accept='.jpg, .jpeg, .png'
                        onChange={(e) => {
                          const file = get(e, 'target.files.0')
                          upload({file})
                        }}
                      />

                      <div className='FaceBlur__upload__box__wrapper'>
                        <div className='FaceBlur__upload__box__wrapper-inner'>
                          <div className='FaceBlur__upload__box__label'>
                            <img
                              className='FaceBlur__upload__box__label__img'
                              alt='FaceBlur__upload__box__label__img'
                              src='/images/FaceBlur__upload__box__label__img.svg'
                            />

                            <p
                              className='FaceBlur__upload__box__label__p'
                              dangerouslySetInnerHTML={{
                                __html: t('pages.demo.faceblur.upload.label'),
                              }}
                            />
                            <div className='Buttons Buttons--jc-c'>
                              <a
                                className='FaceBlur__button'
                                disabled={!isRecaptchaValidated || isUploading}
                              >
                                {isUploading
                                  ? t('layout.loading')
                                  : !isRecaptchaValidated
                                  ? t('layout.preparing')
                                  : t('pages.demo.shared.upload_form.submit')}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      <a
                        className='FaceBlur__upload__box__overlay'
                        onDrop={(e) => {
                          e.preventDefault()

                          isHoveringSet(false)

                          const file = get(e, 'dataTransfer.files[0]')
                          upload({file})
                        }}
                        onDragOver={(e) => {
                          e.preventDefault()
                        }}
                        onDragEnter={(e) => {
                          isHoveringSet(true)
                        }}
                        onDragLeave={(e) => {
                          isHoveringSet(false)
                        }}
                      />
                    </label>
                  )
                })()}
              </div>
            </div>
          </div>

          <div className='FaceBlur__upload__right'>
            <div className='Anchor' id='result'></div>

            <img
              className={className('FaceBlur__upload__right__img', {
                'FaceBlur__upload__right__img--uploaded':
                  isUploading || isUploadedFirstTime,
              })}
              alt='FaceBlur__upload__right__img'
              src={
                isUploadedFirstTime &&
                imgDisplayMode == 1 &&
                imgBlurSrc &&
                !isUploading
                  ? imgBlurSrc
                  : imgSrc
              }
            />

            {isUploading && (
              <div className='FaceBlur__upload__right__overlay'>
                {t('layout.loading')}
              </div>
            )}

            {isError && (
              <div className='FaceBlur__upload__right__overlay'>
                เกิดข้อผิดพลาด โปรดลองใหม่อีกครั้ง
              </div>
            )}

            {isUploadedFirstTime && imgBlurSrc && !isUploading && !isError && (
              <div className='FaceBlur__upload__right__control'>
                <a
                  className='FaceBlur__button FaceBlur__button--trans'
                  onClick={() => {
                    if (__windowWidth.current <= 1320) {
                      document
                        .querySelector('#upload')
                        .scrollIntoView({behavior: 'smooth'})
                    }
                  }}
                >
                  อัปโหลดรูปอีกครั้ง
                </a>

                <div className='FaceBlur__upload__right__control__wrapper'>
                  <a
                    className={className('FaceBlur__button', {
                      'FaceBlur__button--trans': imgDisplayMode == 1,
                    })}
                    onClick={() => {
                      imgDisplayModeSet(0)
                    }}
                  >
                    Original
                  </a>

                  <a
                    className={className('FaceBlur__button', {
                      'FaceBlur__button--trans': imgDisplayMode == 0,
                    })}
                    onClick={() => {
                      imgDisplayModeSet(1)
                    }}
                  >
                    Processed
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='Anchor' id='contact'></div>
        <div className='FaceBlur__contact'>
          <div className='container'>
            <h3
              className='FaceBlur__contact__h3'
              dangerouslySetInnerHTML={{
                __html: t('pages.demo.faceblur.prevent'),
              }}
            />

            <p className='FaceBlur__contact__p'>
              {t('pages.demo.contact.message')}{' '}
              <strong>
                <a href='mailto:sales@datawow.io'>sales@datawow.io</a>{' '}
                <br className='Br Br--less-than--smol' />
                {t('pages.demo.contact.or')}{' '}
                <a href='tel:020245560'>02-024-5560</a>
              </strong>
            </p>

            <form
              className='FaceBlur__contact__form'
              ref={ref}
              onSubmit={handleSubmit}
            >
              <input
                type='text'
                className='FaceBlur__contact__form__input'
                placeholder={t('pages.demo.contact.form.name')}
                name='name'
                required
              />

              <input
                type='text'
                className='FaceBlur__contact__form__input'
                placeholder={t('pages.demo.contact.email')}
                name='email'
                required
              />

              <input
                type='text'
                className='FaceBlur__contact__form__input'
                placeholder={t('pages.demo.contact.form.phone')}
                name='tel'
              />

              <button
                className='FaceBlur__button'
                disabled={!isRecaptchaValidated || isLoading}
              >
                {isSuccess
                  ? t('layout.GetStarted.form.sent')
                  : isLoading
                  ? t('layout.loading')
                  : !isRecaptchaValidated
                  ? t('layout.preparing')
                  : t('pages.demo.contact.form.submit')}
              </button>
            </form>

            <img
              className='FaceBlur__contact__img'
              alt='FaceBlur__contact__img'
              src='/images/FaceBlur__contact__img.svg'
            />

            <p
              className='FaceBlur__contact__footer'
              dangerouslySetInnerHTML={{
                __html: t('pages.demo.contact.address'),
              }}
            />

            <p className='FaceBlur__contact__footer'>
              {t('pages.demo.contact.email')}:{' '}
              <a href='mailto:sales@datawow.io'>sales@datawow.io</a> <br />
              {t('pages.demo.contact.tel')}:{' '}
              <a href='tel:020245560'>02-024-5560</a>
            </p>

            <div className='FaceBlur__contact__socials'>
              <a
                className='FaceBlur__contact__socials__a'
                href='https://www.linkedin.com/company/datawowio/'
                target='_blank'
                rel='nofollow noopener noreferrer'
              >
                <img
                  className='FaceBlur__contact__socials__a__img'
                  alt='FaceBlur__contact__socials__a__img'
                  src='/images/FaceBlur__contact__socials__a__img.svg'
                />
              </a>
            </div>
          </div>
        </div>

        <div className='FaceBlur__footer'>
          <div className='container'>
            <p className='FaceBlur__footer__p'>
              &copy; {moment().year()} Data Wow Co., Ltd. All Rights Reserved.
            </p>

            <div className='FaceBlur__footer__links'>
              {[
                {
                  label: 'Privacy Policy',
                  href: '/policy',
                },
              ].map((x, i) => (
                <Link href={get(x, 'href')} key={i}>
                  <a className='FaceBlur__footer__links__a'>
                    {get(x, 'label')}
                  </a>
                </Link>
              ))}
            </div>

            <p className='FaceBlur__footer__p'>Made with ♥ in Bangkok</p>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

FaceBlur.propTypes = {}

export default FaceBlur
